import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useContact } from '../context/ContactContext';
import { API_URL } from '../config/api';
import './SoftPopup.css';

const CAR_RENTALS_TFN = '+1 (844) 735-6222';
const CAR_RENTALS_TFN_RAW = '+18447356222';

const SERVICES = [
  { icon: '🛫', label: 'New Booking'   },
  { icon: '✏️', label: 'Flight Change' },
  { icon: '❌', label: 'Cancellation'  },
  { icon: '💺', label: 'Seat Upgrade'  },
  { icon: '💰', label: 'Refund Help'   },
  { icon: '🧳', label: 'Baggage Help'  },
];

/* ── Inline SVG: airline customer service agent ── */
const AgentSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 200 260"
    fill="none"
    className="sp-agent-img"
    aria-hidden="true"
  >
    {/* body / uniform */}
    <rect x="55" y="148" width="90" height="95" rx="18" fill="#1e40af"/>
    <rect x="68" y="148" width="64" height="40" rx="8" fill="#2563eb"/>
    {/* tie */}
    <polygon points="100,162 107,178 100,200 93,178" fill="#facc15"/>
    {/* lapels */}
    <polygon points="68,148 90,148 80,188" fill="#1d4ed8"/>
    <polygon points="132,148 110,148 120,188" fill="#1d4ed8"/>
    {/* name badge */}
    <rect x="85" y="162" width="30" height="16" rx="4" fill="white" opacity="0.9"/>
    <rect x="89" y="166" width="22" height="2" rx="1" fill="#94a3b8"/>
    <rect x="91" y="170" width="16" height="2" rx="1" fill="#cbd5e1"/>

    {/* arms */}
    <rect x="24" y="152" width="36" height="22" rx="11" fill="#1e40af" transform="rotate(-18,42,163)"/>
    <rect x="140" y="152" width="36" height="22" rx="11" fill="#1e40af" transform="rotate(18,158,163)"/>
    {/* hands */}
    <ellipse cx="30" cy="185" rx="14" ry="12" fill="#fcd5a0"/>
    <ellipse cx="170" cy="185" rx="14" ry="12" fill="#fcd5a0"/>

    {/* neck */}
    <rect x="88" y="128" width="24" height="26" rx="10" fill="#fcd5a0"/>

    {/* head */}
    <ellipse cx="100" cy="108" rx="38" ry="40" fill="#fcd5a0"/>

    {/* hair */}
    <ellipse cx="100" cy="74" rx="38" ry="16" fill="#1e293b"/>
    <rect x="62" y="74" width="76" height="20" rx="10" fill="#1e293b"/>
    <ellipse cx="67" cy="96" rx="8" ry="16" fill="#1e293b"/>
    <ellipse cx="133" cy="96" rx="8" ry="16" fill="#1e293b"/>

    {/* eyes */}
    <ellipse cx="87" cy="108" rx="5" ry="6" fill="white"/>
    <ellipse cx="113" cy="108" rx="5" ry="6" fill="white"/>
    <ellipse cx="88" cy="109" rx="3" ry="3.5" fill="#1e293b"/>
    <ellipse cx="114" cy="109" rx="3" ry="3.5" fill="#1e293b"/>
    <ellipse cx="89" cy="108" rx="1" ry="1.2" fill="white"/>
    <ellipse cx="115" cy="108" rx="1" ry="1.2" fill="white"/>

    {/* eyebrows */}
    <path d="M82 101 Q87 98 92 101" stroke="#1e293b" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M108 101 Q113 98 118 101" stroke="#1e293b" strokeWidth="2.2" strokeLinecap="round"/>

    {/* smile */}
    <path d="M90 122 Q100 130 110 122" stroke="#e07b5a" strokeWidth="2.5" strokeLinecap="round" fill="none"/>

    {/* headset band */}
    <path d="M64 100 Q64 62 100 62 Q136 62 136 100" stroke="#334155" strokeWidth="5" fill="none" strokeLinecap="round"/>
    {/* ear cups */}
    <rect x="56" y="96" width="16" height="22" rx="8" fill="#334155"/>
    <rect x="128" y="96" width="16" height="22" rx="8" fill="#334155"/>
    <rect x="60" y="100" width="8" height="14" rx="4" fill="#64748b"/>
    <rect x="132" y="100" width="8" height="14" rx="4" fill="#64748b"/>
    {/* mic boom */}
    <path d="M128 114 Q148 118 150 130" stroke="#334155" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
    <ellipse cx="151" cy="133" rx="6" ry="5" fill="#334155"/>
    <ellipse cx="151" cy="133" rx="3.5" ry="3" fill="#94a3b8"/>

    {/* stars / sparkles */}
    <text x="14" y="80" fontSize="18" fill="#facc15" opacity="0.85">✦</text>
    <text x="166" y="68" fontSize="12" fill="#a78bfa" opacity="0.9">✦</text>
    <text x="175" y="105" fontSize="9" fill="#facc15" opacity="0.75">✦</text>
  </svg>
);

const SoftPopup = () => {
  const { contactSettings, siteSettings } = useContact();
  const location = useLocation();
  const isCarRentals = location.pathname === '/car-rentals';
  const tfn      = isCarRentals ? CAR_RENTALS_TFN : (contactSettings?.tfn || '+1-888-859-0441');
  const tfnClean = isCarRentals ? CAR_RENTALS_TFN_RAW : tfn.replace(/[^0-9+]/g, '');

  const cfg        = siteSettings?.popup || {};
  const enabled    = cfg.enabled !== false;
  const devices    = cfg.devices || { mobile: true, tablet: true, desktop: true };
  const delayMs    = (cfg.delaySeconds    ?? 5)  * 1000;
  const autoHideMs = (cfg.autoHideSeconds ?? 20) * 1000;
  const reshowMs   = (cfg.reshowSeconds   ?? 60) * 1000;

  const [visible,   setVisible]   = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [device,    setDevice]    = useState('desktop');

  const reshowRef   = useRef(null);
  const autoHideRef = useRef(null);

  const detectDevice = useCallback(() => {
    const w = window.innerWidth;
    if (w <= 600)  return 'mobile';
    if (w <= 1024) return 'tablet';
    return 'desktop';
  }, []);

  useEffect(() => {
    setDevice(detectDevice());
    const h = () => setDevice(detectDevice());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [detectDevice]);

  const isAllowed = enabled && devices[device] !== false;

  // initial show
  useEffect(() => {
    if (!isAllowed || dismissed) return;
    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed, dismissed]);

  // auto-hide + reshow
  useEffect(() => {
    if (!visible || dismissed) return;
    autoHideRef.current = setTimeout(() => {
      setVisible(false);
      if (!dismissed && isAllowed) {
        reshowRef.current = setTimeout(() => setVisible(true), reshowMs);
      }
    }, autoHideMs);
    return () => {
      clearTimeout(autoHideRef.current);
      clearTimeout(reshowRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dismissed]);

  const trackCall = useCallback(() => {
    fetch(`${API_URL}/api/call-tracking/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: window.location.pathname,
        section: 'popup',
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setDismissed(true);
    clearTimeout(reshowRef.current);
    clearTimeout(autoHideRef.current);
  }, []);

  const handleCall = useCallback((e) => {
    trackCall();
  }, [trackCall]);

  if (!isAllowed || !visible) return null;

  return (
    <div className="sp-backdrop" onClick={handleClose} role="dialog" aria-modal="true" aria-label="Contact our airline experts">

      <div className="sp-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Close ── */}
        <button className="sp-close" onClick={handleClose} aria-label="Close">✕</button>

        {/* ── Hero band ── */}
        <div className="sp-hero">
          <div className="sp-avail-badge">
            <span className="sp-green-dot" />
            Live Agent Available
          </div>

          <div className="sp-hero-text">
            <h2 className="sp-hero-title">
              Talk to an Airline<br />Expert Right Now
            </h2>
            <p className="sp-hero-sub">
              Book, change, or cancel your flight.<br />
              Our specialists are standing by 24/7.
            </p>
          </div>

          <div className="sp-agent-wrap">
            <AgentSVG />
          </div>
        </div>

        {/* ── Body ── */}
        <div className="sp-body">

          {/* TFN click-to-call */}
          <a
            href={`tel:${tfnClean}`}
            className="sp-tfn-card"
            onClick={handleCall}
            aria-label={`Call us at ${tfn}`}
          >
            <div className="sp-ring-icon">📞</div>
            <div>
              <div className="sp-tfn-label">Tap to Call — Free &amp; Instant</div>
              <div className="sp-tfn-number">{tfn}</div>
            </div>
            <span className="sp-tfn-arrow">›</span>
          </a>

          {/* Services grid */}
          <div className="sp-grid">
            {SERVICES.map((s) => (
              <div className="sp-service-card" key={s.label}>
                <span className="sp-service-icon">{s.icon}</span>
                {s.label}
              </div>
            ))}
          </div>

          {/* CTA */}
          <a href={`tel:${tfnClean}`} className="sp-cta-btn" onClick={handleCall}>
            📲&nbsp; Call Now for Instant Help
          </a>

          <p className="sp-fine-print">
            ⏰ Available 24/7 &nbsp;·&nbsp; No Hold Time &nbsp;·&nbsp; Free to Call<br />
            Calls may be recorded for quality &amp; training purposes.
          </p>

        </div>
      </div>
    </div>
  );
};

export default SoftPopup;
